import { Action, createAction, createAsyncAction, createReducer } from 'typesafe-actions';
import { produce } from 'immer';
import type { ReduxError } from '@universal/errors';
import { ChargeTestType, RangeTestType } from '../../../../types/testType';
import { ArticleType, ProductArticleType } from '../../../../types/articleType';
import { BenefitsType } from '../../../../types/benefitsType';
import { CenterArticleType } from '../../../../types/centerArticleType';
import { CenterProductArticleType } from '../../../../types/centerProductArticleType';
import { LocalDepartmentType } from '../../../../types/localDepartmentType';
import { EventType } from '../../../../types/EventType';
import { PracticeTrackType } from '../../../../types/PracticeTrackType';
import { TopicPageAppPropertiesType } from '../../../../types/CategoryAppType';
import { FaqArticleType } from '../../../../types/faqArticleType';
import { DynamicFormArticleType } from '../../../../types/DynamicFormArticleType';
import { BenefitOverviewType } from '../../../../types/BenefitsOverviewType';

// Actions
export enum Actions {
  GET_DOCUMENT = 'GET_DOCUMENT',
  GET_DOCUMENT_SUCCESS = 'GET_DOCUMENT_SUCCESS',
  GET_DOCUMENT_FAIL = 'GET_DOCUMENT_FAIL',
  GET_DOCUMENT_CANCEL = 'GET_DOCUMENT_CANCEL',
  SET_DOCUMENT_FAVORITE = 'SET_DOCUMENT_FAVORITE',
}

export type DocumentData =
  | ArticleType
  | ProductArticleType
  | RangeTestType
  | ChargeTestType
  | BenefitsType
  | CenterArticleType
  | CenterProductArticleType
  | LocalDepartmentType
  | EventType
  | PracticeTrackType
  | TopicPageAppPropertiesType
  | FaqArticleType
  | DynamicFormArticleType
  | BenefitOverviewType;

export type MappedData = {
  data?: DocumentData;
  meta: { isUpdating: boolean; fetchedAt?: string; isLoggedin?: boolean };
  error?: ReduxError;
};

export interface State {
  mappedData: Record<string, MappedData>;
}

export const initialState: State = {
  mappedData: {},
};

export const actions = {
  getDocument: createAsyncAction(
    Actions.GET_DOCUMENT, // request payload creator
    Actions.GET_DOCUMENT_SUCCESS, // success payload creator
    Actions.GET_DOCUMENT_FAIL, // failure payload creator
    Actions.GET_DOCUMENT_CANCEL, // optional cancel payload creator
  )<
    [string, { token?: string }],
    [DocumentData, { id: string }],
    [ReduxError, { id: string }],
    [undefined, { id: string }]
  >(),
  setDocumentFavorite: createAction(Actions.SET_DOCUMENT_FAVORITE)<{ slug: string; isFavorite: boolean }>(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getDocument.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.mappedData[action.payload] = {
        data: draftState.mappedData[action.payload]?.data || undefined,
        meta: {
          isUpdating: true,
          fetchedAt: draftState.mappedData[action.payload]?.meta.fetchedAt || undefined,
          isLoggedin: !!action.meta.token,
        },
      };
    }),
  )
  .handleAction(actions.getDocument.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.mappedData[action.meta.id] = {
        meta: {
          isUpdating: false,
          fetchedAt: new Date().toISOString(),
          isLoggedin: state.mappedData[action.meta.id].meta.isLoggedin,
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        data: action.payload,
      };
    }),
  )
  .handleAction(actions.getDocument.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.mappedData[action.meta.id] = { meta: { isUpdating: false }, error: action.payload };
    }),
  )
  .handleAction(actions.getDocument.cancel, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.mappedData[action.meta.id] = { meta: { isUpdating: false }, error: action.payload };
    }),
  )
  .handleAction(actions.setDocumentFavorite, (state = initialState, action) =>
    produce(state, (draftState) => {
      const entry = draftState.mappedData[action.payload.slug];
      if (entry?.data && 'isFavorite' in entry.data) {
        (entry.data as BenefitsType).isFavorite = action.payload.isFavorite;
      }
    }),
  );
